// TanStack Query hooks, one per route, keyed so hub events can invalidate them (hub/useHub.ts AFFECTS).
import { useCallback, useEffect } from "react";
import { useMutation, useQuery, useQueryClient, type UseQueryOptions } from "@tanstack/react-query";
import { api } from "./client";
import type { Account, AuditEntry, Batch, BatchList, BatchSchedule, Dashboard, Intake, IntakeMessage, Invoice, InvoiceDetail, LiveData, LivePatch, MatchingSwitch, Metrics, NeedsYouSummary, Page, PortalUser, PurchaseOrderPage, ReceivedEmail, Settings, SupplierMetrics, Syncore, UploadJob } from "./types";
import { useSession } from "../auth/SessionProvider";

type Query = Record<string, string | number | boolean | null | undefined>;

function useTenantKey(): string {
  const s = useSession();
  return s.tenantId ?? "none";
}

// Polling: every 5 s while the hub is down. While it is up, events do the work, but a slow poll stays on as the safety
// net. The live snapshot is patched by the hub as things change and the API checks SQL every second for changes whose
// event went missing, so while the hub is up the snapshot is only read again each minute; while it's down, every 2 s.
const live = { refetchInterval: 5000 as number | false, refetchOnWindowFocus: true, snapshotInterval: 2000 };

export function setLivePolling(on: boolean) {
  live.refetchInterval = on ? 5000 : 15000;
  live.snapshotInterval = on ? 2000 : 60_000;
}

const newer = (a: Invoice, b: Invoice) => Date.parse(a.updatedAt) > Date.parse(b.updatedAt);
const byReceived = (a: Invoice, b: Invoice) => a.receivedAt.localeCompare(b.receivedAt);

/** A fresh snapshot decides which invoices there are; of each, the newer copy is kept (a patch can be ahead of a snapshot read from another API instance). */
export function mergeLive(previous: LiveData | undefined, fresh: LiveData): LiveData {
  if (!previous) return fresh;
  const kept = new Map(previous.invoices.map((i) => [i.id, i]));
  return { ...fresh, invoices: fresh.invoices.map((i) => { const k = kept.get(i.id); return k && newer(k, i) ? k : i; }) };
}

/** A hub patch: changed and new invoices replace what is held unless what is held is newer; removed ones go; the batches are replaced when sent. */
export function applyLivePatch(data: LiveData | undefined, patch: LivePatch): LiveData | undefined {
  if (!data) return data;
  const all = new Map(data.invoices.map((i) => [i.id, i]));
  for (const id of patch.removed) all.delete(id);
  for (const i of patch.invoices) { const held = all.get(i.id); if (!held || !newer(held, i)) all.set(i.id, i); }
  return { ...data, invoices: [...all.values()].sort(byReceived), batches: patch.batches ?? data.batches };
}

/**
 * The tenant's live state (GET /live), one cached query that every live list selects from: read on open, patched by
 * the hub's live.patch events, and read again each minute (every 5 s while the hub is down). It is served from the
 * API's memory, so reading it costs no SQL (scale-fix.md).
 */
export function useLive<T>(select: (data: LiveData) => T, enabled = true) {
  const t = useTenantKey();
  const queryClient = useQueryClient();
  return useQuery({
    queryKey: ["live", t],
    queryFn: async () => mergeLive(queryClient.getQueryData<LiveData>(["live", t]), await api<LiveData>("/live")),
    select,
    enabled,
    staleTime: 30_000,
    refetchInterval: () => live.snapshotInterval,
  });
}

/** A live list in the shape of an /invoices page, for screens written against one. */
function asPage(items: Invoice[]): Page<Invoice> {
  return { items, page: 1, pageSize: items.length, total: items.length };
}

/** Live invoices passing `keep`, oldest first. */
export function useLiveInvoices(keep: (i: Invoice) => boolean, enabled = true) {
  return useLive((d) => asPage(d.invoices.filter(keep)), enabled);
}

export function useDashboard() {
  const t = useTenantKey();
  return useQuery({ queryKey: ["dashboard", t], queryFn: () => api<Dashboard>("/dashboard"), refetchInterval: () => live.refetchInterval, refetchOnWindowFocus: true });
}

export function useSettings(options?: Partial<UseQueryOptions<Settings>>) {
  const t = useTenantKey();
  return useQuery({ queryKey: ["settings", t], queryFn: () => api<Settings>("/settings"), staleTime: 30_000, ...options });
}

export function useAccount() {
  const t = useTenantKey();
  return useQuery({ queryKey: ["account", t], queryFn: () => api<Account>("/account"), staleTime: 60_000 });
}

export function useSyncore() {
  const t = useTenantKey();
  return useQuery({ queryKey: ["syncore", t], queryFn: () => api<Syncore>("/syncore"), staleTime: 30_000 });
}

export function useIntake() {
  const t = useTenantKey();
  return useQuery({ queryKey: ["intake", t], queryFn: () => api<Intake>("/intake"), staleTime: 30_000 });
}

export type SettingsSection = "company" | "intake" | "matching" | "syncore" | "approving" | "users" | "plan";

/** Which Settings sections need something: the amber dot on the nav and on Settings in the sidebar. */
export function useSettingsAttention(): Record<SettingsSection, boolean> {
  const syncore = useSyncore();
  const intake = useIntake();
  const account = useAccount();
  const Y = syncore.data;
  const I = intake.data;
  const A = account.data;
  return {
    company: false,
    intake: !!I && (!I.enabled || (I.allowList.enabled && I.allowList.entries.length === 0)),
    matching: false,
    syncore: !!Y && (!Y.lastApiOk || !Y.lastLoginOk || !!Y.credentialHoldUntil),
    approving: false,
    users: false,
    plan: !!A && A.allowance > 0 && A.usage.vouched / A.allowance >= 0.8,
  };
}

export function useIntakeMessages(kind: "received" | "rejected", page = 1, pageSize = 50) {
  const t = useTenantKey();
  return useQuery({ queryKey: ["intake", t, kind, page, pageSize], queryFn: () => api<Page<IntakeMessage>>(`/intake/${kind}`, { query: { page, pageSize } }), refetchInterval: () => live.refetchInterval });
}

/** Reports, Received emails: fetched when the tab opens and when a filter changes; no polling. */
export function useReceivedEmails(query: Query, enabled = true) {
  const t = useTenantKey();
  return useQuery({ queryKey: ["emails", t, query], queryFn: () => api<Page<ReceivedEmail>>("/intake/emails", { query }), enabled, placeholderData: (previous) => previous });
}

/** `pollMs` makes this list poll at its own pace whatever the hub is doing: the Queue uses it for a batch while it releases. */
export function useInvoices(query: Query, enabled = true, pollMs?: number) {
  const t = useTenantKey();
  return useQuery({ queryKey: ["invoices", t, query], queryFn: () => api<Page<Invoice>>("/invoices", { query }), enabled, refetchInterval: () => pollMs ?? live.refetchInterval, placeholderData: (previous) => previous });
}

const waitingForAPerson = (i: Invoice) => (i.status === "NeedsReview" || i.status === "Failed") && !i.resolved;

/** The review queue: everything needing review or failed, not yet resolved, from the live state. */
export function useNeedsYou() {
  return useLiveInvoices(waitingForAPerson);
}

export function useNeedsYouSummary() {
  const t = useTenantKey();
  return useQuery({ queryKey: ["needs", t, "summary"], queryFn: () => api<NeedsYouSummary>("/invoices/needs-you/summary"), refetchInterval: () => live.refetchInterval });
}

export function useSuppliers() {
  const t = useTenantKey();
  return useQuery({ queryKey: ["invoices", t, "suppliers"], queryFn: () => api<string[]>("/invoices/suppliers"), staleTime: 60_000 });
}

export function useInvoice(id: string | null | undefined) {
  const t = useTenantKey();
  return useQuery({ queryKey: ["invoice", t, id], queryFn: () => api<InvoiceDetail>(`/invoices/${id}`), enabled: !!id, refetchInterval: () => live.refetchInterval });
}

/** Loads an invoice into the cache ahead of time, so the slide-out shows the next one without a loading state. */
export function usePrefetchInvoice() {
  const t = useTenantKey();
  const queryClient = useQueryClient();
  return useCallback((id: string) => void queryClient.prefetchQuery({ queryKey: ["invoice", t, id], queryFn: () => api<InvoiceDetail>(`/invoices/${id}`), staleTime: 30_000 }), [queryClient, t]);
}

export function useInvoiceAudit(id: string | null | undefined) {
  const t = useTenantKey();
  return useQuery({ queryKey: ["invoice", t, id, "audit"], queryFn: () => api<AuditEntry[]>(`/invoices/${id}/audit`), enabled: !!id });
}

/** The tenant's recent batches and its release policy, from the live state: a release started by anyone, on any API instance, arrives as a patch. */
export function useBatches() {
  return useLive((d): BatchList => ({ items: d.batches, page: 1, pageSize: d.batches.length, total: d.batches.length, policy: d.policy }));
}

export function useBatch(id: string | null | undefined) {
  const t = useTenantKey();
  return useQuery({ queryKey: ["batches", t, "one", id], queryFn: () => api<{ batch: Batch; invoices: Invoice[] }>(`/batches/${id}`), enabled: !!id });
}

export function useBatchSchedule() {
  const t = useTenantKey();
  return useQuery({ queryKey: ["batches", t, "schedule"], queryFn: () => api<BatchSchedule>("/batches/schedule"), staleTime: 60_000 });
}

export function usePurchaseOrder(job: number | null, po: number | null) {
  const t = useTenantKey();
  return useQuery({ queryKey: ["po", t, job, po], queryFn: () => api<PurchaseOrderPage>(`/purchase-orders/${job}/${po}`), enabled: !!job && !!po, retry: false });
}

export function usePurchaseOrders(job: number | null) {
  const t = useTenantKey();
  return useQuery({ queryKey: ["po", t, job], queryFn: () => api<{ pos: { number: number; supplier: string | null; status: string; total: number | null }[] }>(`/purchase-orders/${job}`), enabled: !!job, retry: false });
}

export function useUsers() {
  const t = useTenantKey();
  return useQuery({ queryKey: ["users", t], queryFn: () => api<PortalUser[]>("/users") });
}

export function useAudit(query: Query) {
  const t = useTenantKey();
  return useQuery({ queryKey: ["audit", t, query], queryFn: () => api<Page<AuditEntry>>("/audit", { query }), placeholderData: (previous) => previous });
}

export function useMetrics(query: Query, enabled = true) {
  const t = useTenantKey();
  return useQuery({ queryKey: ["metrics", t, query], queryFn: () => api<Metrics>("/metrics", { query }), enabled });
}

export function useSupplierMetrics(query: Query) {
  const t = useTenantKey();
  return useQuery({ queryKey: ["metrics", t, "suppliers", query], queryFn: () => api<SupplierMetrics[]>("/metrics/suppliers", { query }) });
}

export function useMatchingSwitches() {
  return useQuery({ queryKey: ["reference", "matching-switches"], queryFn: () => api<MatchingSwitch[]>("/reference/matching-switches"), staleTime: Infinity });
}

/** One uploaded zip's unpacking; asked again every two seconds while it is still running. */
export function useUploadJob(id: string | null) {
  const t = useTenantKey();
  return useQuery({
    queryKey: ["uploads", t, id],
    queryFn: () => api<UploadJob>(`/intake/uploads/${id}`),
    enabled: !!id,
    refetchInterval: (query) => (query.state.data?.status === "Unpacking" ? 2000 : false),
  });
}

/** The person's own most recent uploads they haven't dismissed, newest first, so an unpacking still running after a reload is shown. */
export function useUploads(take = 5) {
  const t = useTenantKey();
  return useQuery({ queryKey: ["uploads", t, "list", take], queryFn: () => api<UploadJob[]>("/intake/uploads", { query: { take } }), staleTime: 5_000 });
}

/** Dismisses an upload's card for good, on every browser: gone from the list at once, then confirmed by the server. */
export function useDismissUpload() {
  const t = useTenantKey();
  const queryClient = useQueryClient();
  return useCallback((id: string) => {
    queryClient.setQueriesData<UploadJob[]>({ queryKey: ["uploads", t, "list"] }, (jobs) => jobs?.filter((j) => j.id !== id));
    void api(`/intake/uploads/${id}/dismiss`, { method: "POST" })
      .catch(() => { /* the card comes back on the next fetch, and can be dismissed again */ })
      .finally(() => void queryClient.invalidateQueries({ queryKey: ["uploads", t, "list"] }));
  }, [t, queryClient]);
}

/** Seeds each job's own query from a list that already holds it, so a finished upload's card renders at once on return instead of re-fetching the job first. */
export function useSeedUploadJobs(jobs: UploadJob[] | undefined) {
  const t = useTenantKey();
  const queryClient = useQueryClient();
  useEffect(() => {
    for (const j of jobs ?? []) if (!queryClient.getQueryData(["uploads", t, j.id])) queryClient.setQueryData(["uploads", t, j.id], j);
  }, [jobs, t, queryClient]);
}

/** A mutation that refreshes the given query families when it succeeds. */
export function useApiMutation<TVars, TResult = unknown>(fn: (vars: TVars) => Promise<TResult>, invalidate: string[][] = [["invoices"], ["invoice"], ["dashboard"], ["needs"], ["batches"], ["live"]]) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSuccess: async () => {
      await Promise.all(invalidate.map((key) => queryClient.invalidateQueries({ queryKey: key })));
    },
  });
}
